package com.tregz.tremenz.ui.area

import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/** Aspect ratio the persistent player is laid out at, whatever the viewport shape. */
private const val PLAYER_ASPECT = 16f / 9f

/**
 * Height tab content needs before it is worth browsing where it sits, under the player.
 *
 * Below this, a tab has to be able to slide over the player to be usable at all.
 */
val MinTabHeight: Dp = 180.dp

/**
 * Height the persistent player occupies at [viewportWidth], or zero when it is not mounted.
 *
 * Constrained by [viewportHeight] so the player stays between the top and bottom toolbars.
 */
fun playerHeight(
    viewportWidth: Dp,
    hasPlayer: Boolean,
    viewportHeight: Dp = Dp.Infinity
): Dp = if (hasPlayer) {
    (viewportWidth / PLAYER_ASPECT).coerceAtMost(viewportHeight)
} else 0.dp

/** Height left for tab content once the player has taken its slice of [viewportHeight]. */
fun tabContentHeight(viewportWidth: Dp, viewportHeight: Dp, hasPlayer: Boolean): Dp =
    (viewportHeight - playerHeight(viewportWidth, hasPlayer, viewportHeight)).coerceAtLeast(0.dp)

/**
 * True when tab content is too short to browse in place and clicking a tab should expand it over
 * the player instead. Without a player there is nothing to overlay, so it never applies.
 */
fun isTabConstrained(
    availableTabHeight: Dp,
    hasPlayer: Boolean,
    minTabHeight: Dp = MinTabHeight
): Boolean = hasPlayer && availableTabHeight < minTabHeight

/**
 * Expansion state a tab click lands on.
 *
 * Re-clicking the tab that is already selected toggles expansion (collapsing or expanding the
 * overlay); selecting another tab reveals it when collapsed and keeps it revealed when expanded.
 * Without an expandable player state, tabs do not overlay.
 */
fun nextTabExpanded(
    canExpand: Boolean,
    isExpanded: Boolean,
    isSameTab: Boolean
): Boolean = when {
    !canExpand -> false
    isSameTab -> !isExpanded
    else -> true
}

/**
 * Top inset of the tab content overlay: flush with the top of the viewport when expanded, docked
 * below the player when unconstrained, or retracted to [collapsedPadding] (viewport height) when
 * constrained so the player / photo carousel is fully revealed.
 */
fun tabTopPadding(
    playerHeight: Dp,
    isExpanded: Boolean,
    isConstrained: Boolean = false,
    collapsedPadding: Dp = playerHeight
): Dp = when {
    isExpanded -> 0.dp
    isConstrained -> collapsedPadding
    else -> playerHeight
}
